import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import Form from "../../ui/Form/Form";
import NumberField from "../inputs/NumberField";
import TextField from "../inputs/TextField";
import { IIKIConnectionData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import {
  BULK_UPLOAD_GENERAL_TRANSLATIONS,
  CREATE_IKI_CONNECTION_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";
import { ADD_IKI_CONNECTION_FORM_FIELDS } from "../../constants/addFormFields";
import DragndropContainer from "../fileUpload/DragNdropContainer";
import FileUploaderItem from "../fileUpload/FileUploaderItem";
import { ReactComponent as Key } from "../../assets/icons/key.svg";

interface IProps {
  org: string;
}
const set = new Set();

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({
  data,
  content,
  type,
  name,
}: {
  data: any;
  content: string | ArrayBuffer | null | undefined;
  type: string | undefined;
  name: string | undefined;
}) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_IKI_CONNECTION_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "TEXT":
            return field.isInputInvalid?.(data[field.name]);
          case "NUMBER":
            return isInValidNumberForField(
              data[field.name],
              field.min,
              field.max
            );
          case "FILE":
            return (
              content === "" ||
              content === null ||
              content === undefined ||
              (type !== "application/x-x509-ca-cert" &&
                name &&
                !name.endsWith(".pem") &&
                !name.endsWith(".crt")) ||
              (type !== "application/x-pem-file" &&
                name &&
                !name.endsWith(".pem") &&
                !name.endsWith(".crt"))
            );
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewIKIConnections: React.FC<IProps> = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [certificate, setCertificate] = useState<{
    name: string;
    type: string;
    content?: string | ArrayBuffer | null;
  }>({
    name: "",
    type: "",
    content: "",
  });
  const [dataLoading, setDataLoading] = useState(false);

  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<IIKIConnectionData>({
    "CONNECTION-ID": "",
    "IKI-IPV4-ADDR": "",
    "IKI-PORT": null,
    "IKI-RADIO-SET-IDENTIFIER": null,
    "IWF-RADIO-SET-IDENTIFIER": null,
    "IKI-NAME": "",
    "IKI-CERTIFICATE": "",
  });

  const createIKIConnection = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/iki-connections`,
        method: "POST",
        body: JSON.stringify({
          "CONNECTION-ID": connectionData["CONNECTION-ID"],
          "IKI-IPV4-ADDR": connectionData["IKI-IPV4-ADDR"],
          "IKI-PORT": connectionData["IKI-PORT"],
          "IKI-RADIO-SET-IDENTIFIER":
            connectionData["IKI-RADIO-SET-IDENTIFIER"],
          "IWF-RADIO-SET-IDENTIFIER":
            connectionData["IWF-RADIO-SET-IDENTIFIER"],
          "IKI-CERTIFICATE": certificate.content,
          "IKI-NAME": connectionData["IKI-NAME"],
        }),
        token,
        org,
      });
      successAlert(CREATE_IKI_CONNECTION_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/ikiConnections");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  // This function will remove/delete the file and reset the state

  const handleFileDelete = () => {
    setCertificate({
      name: "",
      type: "",
      content: "",
    });
  };

  // This function returns error subject based on conditions
  const handleErrorSubject = () => {
    let errorSubject = "";
    if (
      (certificate.type !== "application/x-x509-ca-cert" &&
        certificate.name &&
        !certificate.name.endsWith(".pem") &&
        !certificate.name.endsWith(".crt")) ||
      (certificate.type !== "application/x-pem-file" &&
        certificate.name &&
        !certificate.name.endsWith(".pem") &&
        !certificate.name.endsWith(".crt"))
    ) {
      errorSubject =
        BULK_UPLOAD_GENERAL_TRANSLATIONS.InvalidCertificateTypeText;
    }

    if (
      certificate.content !== "" &&
      certificate.content !== null &&
      certificate.content !== undefined
    ) {
      errorSubject =
        BULK_UPLOAD_GENERAL_TRANSLATIONS.InvalidCertificateTypeText;
    }
    return errorSubject;
  };

  const handleCertificateParsing = (file: any) => {
    let fileReader: FileReader;
    try {
      setDataLoading(true);
      fileReader = new FileReader();
      fileReader.readAsText(file);
      fileReader.onloadend = () => {
        const content: string | ArrayBuffer | null = fileReader.result;
        setCertificate({
          ...certificate,
          name: file.name,
          type: file.type,
          content,
        });
        return content;
      };
    } catch (error: any) {
      errorAlert(error);
    } finally {
      setDataLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_IKI_CONNECTION_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IIKIConnectionData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  min?: number;
                  max?: number;
                  disabled?: boolean;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "NUMBER":
                          return (
                            <NumberField
                              onChange={(val) => {
                                set.add(field.name);
                                if (!val || !Number.isNaN(+val))
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val ? +val : undefined,
                                  });
                              }}
                              label={field.label}
                              placeholder={field.label}
                              max={field.max || 0}
                              min={field.min || 0}
                              invalid={
                                set.has(field.name) &&
                                connectionData[field.name] !== 0 &&
                                !connectionData[field.name]
                              }
                              value={connectionData[field.name] as number}
                              invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                              tooltipText={field.tooltipText}
                              required
                            />
                          );
                        case "FILE":
                          return (
                            <div className=" bulk-upload-card">
                              <h4
                                className="import-tab-title"
                                data-cy="tab-title"
                              >
                                Upload New IKI Certificate *
                              </h4>
                              <div
                                className="dragndrop-container"
                                data-cy="dragndrop-container"
                              >
                                <DragndropContainer
                                  labelText={field.label}
                                  filenameStatus={
                                    dataLoading ? "uploading" : "edit"
                                  }
                                  onAddFiles={(fileData) => {
                                    handleCertificateParsing(fileData);
                                  }}
                                  disabled={!!certificate.name}
                                  accept={[
                                    ".pem",
                                    ".crt",
                                    "application/x-x509-ca-cert",
                                    "application/x-pem-file",
                                  ]}
                                />
                              </div>
                              {certificate.name ? (
                                <div className="file-block flex items-center justify-between">
                                  <div className="flex items-center">
                                    <div className="certificate-svg">
                                      <Key />
                                    </div>
                                    <FileUploaderItem
                                      name={BULK_UPLOAD_GENERAL_TRANSLATIONS.ImportTab.handleFileUpoadtext(
                                        certificate.name
                                      )}
                                      data-cy="file-uploader-item"
                                      status={
                                        dataLoading ? "uploading" : "edit"
                                      }
                                      onDelete={handleFileDelete}
                                      invalid={
                                        (certificate.type !==
                                          "application/x-x509-ca-cert" &&
                                          certificate.name &&
                                          !certificate.name.endsWith(".pem") &&
                                          !certificate.name.endsWith(".crt")) ||
                                        (certificate.type !==
                                          "application/x-pem-file" &&
                                          certificate.name &&
                                          !certificate.name.endsWith(".pem") &&
                                          !certificate.name.endsWith(".crt"))
                                      }
                                      errorSubject={handleErrorSubject()}
                                    />
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        default:
                          return (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required
                              disabled={field.disabled}
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_IKI_CONNECTION_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_IKI_CONNECTION_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/ikiconnections")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_IKI_CONNECTION_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_IKI_CONNECTION_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateIKIConnection-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({
              data: connectionData,
              content: certificate.content,
              type: certificate.type,
              name: certificate.name,
            })}
            onClick={createIKIConnection}
            label={CREATE_IKI_CONNECTION_TRANSLATIONS.createButtonLabel}
            dataCy="createIKIConnection-"
          />
        </div>
      </div>
    </>
  );
};

export default NewIKIConnections;
